<template>
  <!-- @leave="onLeave" -->
  <transition-group tag="div" class="fade-logos" :css="false" @before-enter="onBeforeEnter" @enter="onEnter">
    <div v-for="(logo, index) in displayLogos" :key="logo" class="d-flex align-items-center">
      <div class="fade-logo">
        <div class="fade-logo-image">
          <img :src="logo.url" :alt="logo.title" class="active">
        </div>

        <div class="fade-logo-text">
          <p>{{ logo.title }}</p>
          <p v-if="logo.desc">{{ logo.desc }}</p>
        </div>
      </div>

      <span v-if="index + 1 < displayLogos.length" class="fade-logo-seperator"></span>
    </div>
  </transition-group>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { viewportSize, getRandomNumber } from './imports/utils';
import { gsap } from 'gsap';

const props = defineProps({
  logos: {
    type: Array,
    default: null,
    required: true
  },
  responsive: {
    type: Object,
    default: {
      0: 2,
      576: 3,
      // 768: 3,
      // 992: 3,
      // 1369: 3,
    },
    required: false
  }
});

/////////////////////////////////////// Responsive display keys: values
const responsiveKeys = Object.keys(props.responsive);

/////////////////////////////////////// Update viewport on resize
const viewport = ref(viewportSize());

const resizeListener = (e) => {
  Object.assign(viewport.value, viewportSize());
  calculateLists();
};

window.addEventListener("resize", resizeListener, {
  passive: true
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', resizeListener);
});

/////////////////////////////////////// Calculate display and rest logos
const displayLogos = ref([]);
const restLogos = ref([]);

let activeResponsiveKey = 0;
let displayLength;

const calculateLists = () => {
  displayLogos.value = [];
  restLogos.value = [];

  responsiveKeys.forEach(key => {
    if (key <= viewport.value.width) {
      activeResponsiveKey = key;
    }
  });

  displayLength = props.responsive[activeResponsiveKey];

  if (displayLength > props.logos.length) {
    for (let idx1 = 0; idx1 <= props.logos.length - 1; idx1++) {
      displayLogos.value.push(props.logos[idx1]);
    }
  } else {
    for (let idx1 = 0; idx1 < displayLength; idx1++) {
      displayLogos.value.push(props.logos[idx1]);
    }
  }

  for (let idx2 = displayLength; idx2 < props.logos.length; idx2++) {
    restLogos.value.push(props.logos[idx2]);
  }
};

calculateLists();

/////////////////////////////////////// Move logos from one list to another
const replaceLogo = () => {

  setTimeout(() => {

    // get random index from display items
    const randomDisplayIndex = getRandomNumber(0, displayLogos.value.length - 1);

    const restItem = restLogos.value[0];
    const displayItem = displayLogos.value[randomDisplayIndex];

    // remove first item from rest list
    restLogos.value.splice(0, 1);

    // and push it randomly to display list
    displayLogos.value.splice(randomDisplayIndex, 0, restItem);

    // remove from display list the random position item
    displayLogos.value.splice(randomDisplayIndex + 1, 1);

    // and push it to rest list
    restLogos.value.push(displayItem);
    // restLogos.value.splice(randomDisplayIndex, 0, displayItem);

    requestAnimationFrame(replaceLogo);

  }, 3000);
}

onMounted(() => {
  if (restLogos.value.length > 0)
    requestAnimationFrame(replaceLogo);
});

/////////////////////////////////////// Animations
// const onLeave = (el, done) => {
//   gsap.to(el, {
//     opacity: 0,
//     duration: 1,
//     onComplete: done
//   });
// };

const onBeforeEnter = (el) => {
  const logoEl = el.querySelector('.fade-logo');
  logoEl.style.opacity = 0;
};

const onEnter = (el, done) => {
  const logoEl = el.querySelector('.fade-logo');

  gsap.to(logoEl, {
    opacity: 1,
    duration: 1,
    delay: 0.2,
    onComplete: done
  });
};

</script>

<style lang="scss">
@import "../sass/mixins";
@import "../sass/variables";

.fade-logos {
  justify-content: center;
  display: flex;

  .fade-logo {
    position: relative;
    text-align: center;
    padding: 0px 20px;

    .fade-logo-image {
      width: 120px;
      height: 50px;
      display: inline-flex;
      align-items: center;
      justify-content: center;

      img {
        max-width: 100%;
        max-height: 100%;
      }
    }

    .fade-logo-text {
      margin-top: 5px;
      text-align: center;
      min-height: 60px;

      p {
        font-size: 10px;
        color: $white;
      }
    }
  }

  .fade-logo-seperator {
    display: block;
    width: 1px;
    height: 60%;
    background-color: rgba($white, 0.5);
  }
}

// SM (576px)
@media only screen and (min-width: map-get($grid-breakpoints, "sm")) {
  .fade-logos {
    .fade-logo {
      .fade-logo-image {
        width: 129px;
        height: 50px;
      }
    }
  }
}

// MD (768px)
@media only screen and (min-width: map-get($grid-breakpoints, "md")) {
  .fade-logos {
    .fade-logo {
      padding: 0px 30px;

      .fade-logo-image {
        width: 140px;
        height: 60px;
      }
    }
  }
}

// LG (992px)
@media only screen and (min-width: map-get($grid-breakpoints, "lg")) {
  .fade-logos {
    .fade-logo {
      .fade-logo-image {
        width: 153px;
      }
    }
  }
}

// XL (1200px)
@media only screen and (min-width: map-get($grid-breakpoints, "xl")) {
  .fade-logos {
    .fade-logo {
      .fade-logo-image {
        width: 198px;
        height: 75px;
      }

      .fade-logo-text {
        margin-top: 20px;

        p {
          font-size: 12px;
        }
      }
    }
  }
}

// XXL (1367px)
@media only screen and (min-width: map-get($grid-breakpoints, "xxl")) {
  .fade-logos {
    .fade-logo {
      .fade-logo-image {
        width: 210px;
        height: 80px;
      }
    }
  }
}
</style>